import {
  useDemandData,
  useSiteList,
  useSites,
} from "@inrange/building-manager-api-client";
import { useMemo, useState } from "react";
import { Alert, Button, Form, Spinner } from "react-bootstrap";
import Select from "react-select";
import { DateRangePicker } from "../../components";

interface DemandDataLoadSiteActualsProps {
  onExtractComplete: () => void;
}

const DemandDataLoadSiteActuals = ({
  onExtractComplete,
}: DemandDataLoadSiteActualsProps) => {
  const { siteList } = useSiteList();
  const { actualDemandFromSite } = useDemandData();

  const [selectedSite, setSelectedSite] = useState<{
    value: string;
    label: string;
  } | null>(null);
  const [selectedEsys, setSelectedEsys] = useState<{
    value: number;
    label: string;
  } | null>(null);
  const [startMonth, setStartMonth] = useState<Date | null>(null);
  const [endMonth, setEndMonth] = useState<Date | null>(null);
  const [extracting, setExtracting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const operationalSiteIds = useMemo(
    () =>
      (siteList || [])
        .filter((site) => site.operationalStatus === "operational")
        .map((site) => site.id),
    [siteList]
  );

  const siteQueries = useSites(operationalSiteIds);
  const sitesLoading = siteQueries.some((q) => q.isLoading);

  const operationalSites = useMemo(
    () => siteQueries.flatMap((q) => q.data?.sites || []),
    [siteQueries]
  );

  const siteOptions = operationalSites.map((site) => ({
    value: site.id,
    label: `${site.name}`,
  }));

  const selectedSiteData = selectedSite
    ? operationalSites.find((s) => s.id === selectedSite.value)
    : null;

  const esysEntries = selectedSiteData
    ? Object.entries(selectedSiteData.esys)
    : [];

  const esysOptions = esysEntries.map(([idx, esys]) => ({
    value: Number(idx),
    label: `Esys ${idx}${esys.importGridConnectionId ? ` (${esys.importGridConnectionId})` : ""}`,
  }));

  const handleSiteChange = (
    option: { value: string; label: string } | null
  ) => {
    setSelectedSite(option);
    setSelectedEsys(null);
    setError(null);

    if (option) {
      const site = operationalSites.find((s) => s.id === option.value);
      if (site) {
        const entries = Object.entries(site.esys);
        if (entries.length === 1) {
          const [idx, esys] = entries[0];
          setSelectedEsys({
            value: Number(idx),
            label: `Esys ${idx}${esys.importGridConnectionId ? ` (${esys.importGridConnectionId})` : ""}`,
          });
        }
      }
    }
  };

  const formatMonth = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    return `${year}-${month}`;
  };

  const canExtract =
    selectedSite && selectedEsys && startMonth && endMonth && !extracting;

  const handleExtract = async () => {
    if (!selectedSite || !selectedEsys || !startMonth || !endMonth) return;

    setExtracting(true);
    setError(null);

    try {
      await actualDemandFromSite.mutateAsync({
        siteId: selectedSite.value,
        esysIdx: selectedEsys.value,
        startMonth: formatMonth(startMonth),
        endMonth: formatMonth(endMonth),
      });
      onExtractComplete();
    } catch (err: any) {
      const message =
        err?.response?.data?.message || err?.message || "Unknown error";
      setError(message);
    } finally {
      setExtracting(false);
    }
  };

  return (
    <Form>
      <Form.Group className="mb-3">
        <Form.Label>Site</Form.Label>
        <Select
          options={siteOptions}
          value={selectedSite}
          onChange={handleSiteChange}
          isSearchable
          isLoading={sitesLoading}
          placeholder="Search for an operational site..."
          isClearable
        />
      </Form.Group>
      {selectedSiteData && esysEntries.length > 1 && (
        <Form.Group className="mb-3">
          <Form.Label>Electrical system</Form.Label>
          <Select
            options={esysOptions}
            value={selectedEsys}
            onChange={setSelectedEsys}
            placeholder="Select electrical system..."
            isClearable
          />
        </Form.Group>
      )}
      <Form.Group className="mb-3">
        <Form.Label>Month range</Form.Label>
        <DateRangePicker
          value={{ start: startMonth, end: endMonth }}
          onChange={({ start, end }) => {
            setStartMonth(start);
            setEndMonth(end);
          }}
          mode="month"
          startPlaceholder="Start month"
          endPlaceholder="End month"
        />
      </Form.Group>
      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}
      <Button variant="primary" onClick={handleExtract} disabled={!canExtract}>
        {extracting ? (
          <>
            <Spinner size="sm" className="me-2" />
            Extracting...
          </>
        ) : (
          "Extract"
        )}
      </Button>
    </Form>
  );
};

export default DemandDataLoadSiteActuals;
